import { CommonModule, DOCUMENT } from '@angular/common';
import { afterNextRender, Component, ElementRef, Inject, inject, OnInit, ViewChild, OnDestroy, HostListener } from '@angular/core';
import { IsLoggedIn } from '@core/entity/auth/state/auth.selectors';
import { environment } from '@env/environment';
import { select, Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { DomainService } from '@core/services/domain.service';
import { EventEmitterService } from '@core/services/event-emitter.service';
import { PlatformService } from '@core/services/platform.service';
import { svgIconList } from '@shared/svg-icon-list';
import { SafeHtmlPipe } from '@shared/pipes/safe-html.pipe';
import { AmountFormatPipe } from '@shared/pipes/amount-format.pipe';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterModule } from '@angular/router';
import { MenuBarHttpService } from '@core/entity/menubar/service/menu-bar.http.service';
import { MatMenuModule } from '@angular/material/menu';
import { LogoFaviconHttpService } from '@core/services/logo-favicon-http.service';
import { merge, Observable, Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import Swal, { SweetAlertCustomClass } from 'sweetalert2';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { TranslateService } from '@ngx-translate/core';
import { WalletHttpService } from '@core/services/wallet-http.service';
import { AuthHttpService } from '@core/entity/auth/service/auth.http.service';
import { AuthActions } from '@core/entity/auth/state/auth-action.types';
import { MemberProfileActions } from '@core/entity/member-profile/state/member-profile-action.types';
import { SharedService } from '@core/services/shared.service';
import { PortalMessageHttpService } from '@core/services/portal-message-http.service';
import { getMemberAccountId } from '@shared/utils/member-account-id.util';
import { SystemSettingHttpService } from '@core/services/system-setting-http.service';
import { RequestReceipt } from '@shared/models/request-receipt.model';
import { GameCategoryDataService } from '@core/services/games/gamecategory-data.service';
import { SwiperOptions } from 'swiper/types';
import { SwiperDirective } from '@core/directive/swiper.directive';
import { AppState } from '@store/reducers';
import { LoginModalComponent } from '@shared/login-modal/login-modal.component';
import { SignupModalComponent } from '@shared/signup-modal/signup-modal.component';
import { ForgotCredentialsModalComponent } from '@shared/forgot-credentials-modal/forgot-credentials-modal.component';
import { LanguageModalComponent } from '@shared/language-modal/language-modal.component';

@Component({
  selector: 'app-topbar',
  imports: [CommonModule, TranslateModule, SafeHtmlPipe, MatBadgeModule, RouterModule, MatMenuModule, AmountFormatPipe, SwiperDirective],
  templateUrl: './topbar.component.html',
  styleUrl: './topbar.component.scss',
  host: { ngSkipHydration: 'true' },
  schemas: []
})
export class TopbarComponent implements OnDestroy, OnInit {
  private portalMessageHttpService = inject(PortalMessageHttpService);

  @ViewChild('topbar') topbar: ElementRef;
  @ViewChild('mainContent') mainContent: ElementRef;
  @ViewChild('menubars', { static: false }) menubars!: ElementRef<HTMLElement>;
  private resizeObserver?: ResizeObserver;

  logoLoading: boolean = true;
  logoUrl: any;
  sitePrefix: string = environment.sitePrefix;
  svg: any = svgIconList;

  projectCode = environment.projectCode.toLocaleLowerCase();
  isQP11Dark = this.projectCode == 'qp11-dark';
  haveNewVersion = false;
  latestVersion = '';
  isLoggedIn$: Observable<boolean> = null;
  hasStoredSession = false;
  // The server cannot see the session (auth is localStorage-only), so it would always render
  // the logged-out block and the browser would paint LOGIN/REGISTER before Angular boots.
  // Gating the auth area on this makes SSR render nothing there; the client then draws the
  // correct block, so a logged-in reload goes straight to the member bar with no flash.
  // Assigned in the constructor (platformService is not injected yet at field-init time).
  isBrowser = false;
  userFlag: string;

  private subscriptions: Subscription[] = [];
  customClass: SweetAlertCustomClass = {
    popup: 'customPopup',
    htmlContainer: 'customContent'
  };

  currentCountryCode = environment.defaultCountryCode;
  currentLangCode = environment.defaultLanguage;
  userProfileDetail: any = null;
  userData: any = null;
  availableProvider: any = null;

  isQP2 = ['qp2ax', 'qp2bx', 'qp2cx', 'qp2dx'].includes(environment.projectCode.toLowerCase());
  unreadMessages$: Observable<number> = this.portalMessageHttpService.messageCount$;
  isReload = false;
  memberCurrency = '';
  memberBalance: number;
  walletLoading = true;
  depositData: RequestReceipt = null;
  turnoverAmount: any;
  show_promo_turnover = true;
  isDepositTurnover = false;
  fixed = false;
  isSkeletonMenuLists = true;

  menu: any = [];
  moreItems: any = [];
  menuLists: any = [];

  firstAvailableProviderSL: any;
  firstAvailableProviderFS: any;
  firstAvailableProviderLT: any;

  walletIcon = 'assets/topbar/wallet.png';
  portalIcon = 'assets/topbar/account.png';
  messageIcon = 'assets/topbar/message.png';

  homeIcon = 'assets/topbar/home.png';
  mobileIcon = 'assets/topbar/mobile.png';

  menuBarConfig: SwiperOptions = {
    pagination: { el: '.swiper-pagination', clickable: true },
    allowTouchMove: false,
    slidesPerView: 'auto',
    spaceBetween: 0,
    freeMode: true,
    autoplay: false
  };

  constructor(
    private domainService: DomainService,
    private store: Store<AppState>,
    private eventEmitterService: EventEmitterService,
    private platformService: PlatformService,
    private menuBarHttpService: MenuBarHttpService,
    private router: Router,
    private logoFaviconHttpService: LogoFaviconHttpService,
    private gameCategoryDataService: GameCategoryDataService,
    private dialog: MatDialog,
    private walletHttpService: WalletHttpService,
    private authService: AuthHttpService,
    private loadingBar: LoadingBarService,
    private translateService: TranslateService,
    private sharedService: SharedService,
    private systemSettingHttpService: SystemSettingHttpService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.isBrowser = this.platformService.isBrowser();

    afterNextRender(() => {

      this.gameCategoryDataService.loadAvailableProvidersFromStorage();

      // Start loading menu data
      this.menuBarHttpService.getMenuBar().subscribe((res) => {
        // console.log("getMenuBar : ", res);
        this.menuLists = res;
        this.isSkeletonMenuLists = false;

        // Persist the full menu so subpages can resolve their banner title from the active menu
        // item (activeGameCategoryTab → menu item .name, e.g. "REFERRAL TEST"). Ported from
        // template-02's topbar; without it topbar_menu is never set and every subpage title falls
        // back to its hardcoded string.
        if (this.platformService.isBrowser()) {
          localStorage.setItem('topbar_menu', JSON.stringify(res));
        }

        // CRITICAL: Only now we have real data → recalculate layout
        if (this.platformService.isBrowser()) {
          // Use requestAnimationFrame or setTimeout to ensure DOM is updated
          requestAnimationFrame(() => this.fitMenu());
        } else {
          // SSR: just show all items (safe)
          this.menu = [...res];
          this.moreItems = [];
        }
      });

      // Setup ResizeObserver early (so it's ready when data arrives)
      if (this.platformService.isBrowser()) {
        this.resizeObserver = new ResizeObserver(() => {
          this.fitMenu();
        });

        if (this.menubars?.nativeElement) {
          this.resizeObserver.observe(this.menubars.nativeElement);
        }

        // Optional: one immediate try in case data was already cached
        if (this.menuLists.length > 0) {
          requestAnimationFrame(() => this.fitMenu());
        }
      }
    });

    if (this.platformService.isBrowser()) {
      this.currentCountryCode = localStorage.getItem('country_code') ? localStorage.getItem('country_code').toUpperCase() : (this.domainService.getCountryCode() ?? environment.defaultCountryCode);
      this.currentLangCode = localStorage.getItem('language_code') ? localStorage.getItem('language_code') == 'ZH' ? '中文' : localStorage.getItem('language_code') : (this.domainService.getLanguageCode() ?? environment.defaultLanguage);
      this.userProfileDetail = localStorage.getItem('user_data') ? JSON.parse(localStorage.getItem('user_data')) : null;
      this.userData = JSON.parse(localStorage.getItem('user_data'));

      // Suppress the SSR auth flash: SSR renders logged-out (no localStorage server-side),
      // and the store only flips to logged-in after auth.http.service rehydrates (a 100ms
      // setTimeout on the dev path), so the client briefly shows LOGIN/REGISTER on reload.
      // Seeding this synchronously lets the gate hold those buttons back when a valid token
      // is already stored; genuinely logged-out users (no token) still see them immediately.
      this.hasStoredSession = this.detectStoredSession();

      const userToken = localStorage.getItem('user_token');

      // Wrap localStorage.setItem to intercept changes
      const originalSetItem = localStorage.setItem;
      localStorage.setItem = (key: string, value: string) => {
        originalSetItem.apply(localStorage, [key, value]);

        if (key === 'user_data') {
          // Keep both in step: the template falls back to userData.username when
          // userProfileDetail.username is empty, and userData was previously only ever
          // read once in this constructor -- null for anyone who logged in afterwards.
          this.userProfileDetail = value ? JSON.parse(value) : null;
          this.userData = this.userProfileDetail;
        }
      };
    }
  }

  ngOnInit() {

    this.logoFaviconHttpService.desktop_logo$.subscribe((result) => { this.logoUrl = result; this.logoLoading = false; });

    // Load from cache immediately (if exists)
    this.gameCategoryDataService.loadAvailableProvidersFromStorage();

    // Reactively listen to available providers
    this.gameCategoryDataService.availableProviders$.subscribe((providers) => {
      this.availableProvider = providers;

      if (providers && Object.keys(providers).length > 0) {
        this.updateFirstProviders();
        this.eventEmitterService.onTopBarTabEmitter();
      }
    });

    // Trigger API call if not already loaded
    if (!this.availableProvider) {
      this.gameCategoryDataService.getAllGameProvider().subscribe();
    }

    this.userFlag = `/assets/images/language/${this.currentCountryCode}.png`;
    console.log("ngOnInit currentLangCode : ", this.currentLangCode);
    console.log("userFlag : ", this.userFlag);

    this.isLoggedIn$ = this.store.pipe(select(IsLoggedIn));

    if (this.platformService.isBrowser()) {
      this.getFlag();

      this.isLoggedIn$.subscribe((res) => {
        setTimeout(() => {
          this.getMenuBar();
          this.gameCategoryDataService.getAllGameProvider().subscribe(() => { this.eventEmitterService.onTopBarTabEmitter() });
        }, res ? 500 : 0);
      });

      // Was `if (IsLoggedIn)` -- the imported selector *function*, which is always truthy, so
      // isReload was pinned true for everyone and (with restore() stubbed out) the balance
      // never rendered anything but its skeleton.
      this.subscriptions.push(
        this.isLoggedIn$.subscribe((loggedIn) => {
          if (!loggedIn) {
            this.isReload = false;
            this.walletLoading = false;
            return;
          }
          this.isReload = true;
          this.refreshWalletDisplay();
          this.getDepositTurnoverSetting();
        })
      );

      this.subscribeToWalletEvents();
      this.subscribeToVersionStatus();
    }

    this.unreadMessages$ = this.portalMessageHttpService.messageCount$;

    if (this.platformService.isBrowser()) {
      this.isLoggedIn$.subscribe((res) => {
        setTimeout(() => {
          if (!res) {
            this.portalMessageHttpService.disconnectSocket();
            return;
          }

          if (window.location.pathname === '/gamelaunch' || window.location.pathname === '/launch-game') {
            return;
          }

          const memberAccountId = getMemberAccountId();
          if (!memberAccountId) {
            return;
          }

          this.portalMessageHttpService.getUnread().subscribe((unread) => {
            this.portalMessageHttpService.messageCount$.next(unread);
            this.portalMessageHttpService.messageWebsocket(memberAccountId);
          });
        }, 500);
      });
    }

    if (this.platformService.isBrowser()) {

      this.logoFaviconHttpService.updateLogoAndFavicon();

      this.eventEmitterService.flagEmitter.subscribe(() => {
        this.currentCountryCode = localStorage.getItem('country_code');
        this.currentLangCode = localStorage.getItem('language_code') ? localStorage.getItem('language_code') == 'ZH' ? '中文' : localStorage.getItem('language_code') : this.domainService.getLanguageCode();
        this.getFlag();
      });
    }
  }

  private updateFirstProviders() {
    this.firstAvailableProviderSL = this.availableProvider?.SL?.length > 0
      ? this.availableProvider.SL[0].game_provider_name.replace(/\s/g, '').toLowerCase()
      : null;

    this.firstAvailableProviderFS = this.availableProvider?.FS?.length > 0
      ? this.availableProvider.FS[0].game_provider_name.replace(/\s/g, '').toLowerCase()
      : null;

    this.firstAvailableProviderLT = this.availableProvider?.LT?.length > 0
      ? this.availableProvider.LT[0].game_provider_name.replace(/\s/g, '').toLowerCase()
      : null;
  }

  onOpenDialog(type: string) {
    if (!this.platformService.isBrowser()) return;

    switch (type) {
      case 'login':
        this.dialog.open(LoginModalComponent, { panelClass: 'auth-modal-pane', autoFocus: false });
        break;
      case 'signup':
        this.dialog.open(SignupModalComponent, { panelClass: 'auth-modal-pane', autoFocus: false });
        break;
      case 'forgotpassword':
        this.dialog.open(ForgotCredentialsModalComponent, { panelClass: 'auth-modal-pane', autoFocus: false, data: { type: 'password' } });
        break;
      case 'forgotusername':
        this.dialog.open(ForgotCredentialsModalComponent, { panelClass: 'auth-modal-pane', autoFocus: false, data: { type: 'username' } });
        break;
      // case 'wallet': this.dialog.open(WalletModalComponent, …); break;
      case 'language':
        this.dialog.open(LanguageModalComponent, { panelClass: 'auth-modal-pane', autoFocus: false });
        break;
    }
  }

  onShowReloadModal() {
    // this.dialog.open(PopupServiceWorkerComponent, { width: '800px', data: { type: 'force-reload', newVersion: this.latestVersion } });
  }

  private getFlag() {
    var code = localStorage.getItem('country_code') ? localStorage.getItem('country_code').toUpperCase() : (this.domainService.getCountryCode() ?? environment.defaultCountryCode);
    this.userFlag = `/assets/images/language/${code}.png`;
  }

  /** True when a non-expired session token is already in localStorage (mirrors the
   *  rehydrate check in auth.http.service, so an expired token does not falsely show the
   *  member bar). Used only to avoid the logged-out flash before the store catches up. */
  private detectStoredSession(): boolean {
    try {
      const authRaw = localStorage.getItem('authToken');
      const profileRaw = localStorage.getItem('memberProfile');
      if (!authRaw || !profileRaw) return false;
      const auth = JSON.parse(authRaw);
      if (auth?.expiry_datetime && new Date(auth.expiry_datetime).getTime() <= Date.now()) {
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }

  onFlagError(event: Event) {
    const fallback = `/assets/images/language/${environment.defaultCountryCode}.png`;
    const img = event.target as HTMLImageElement;
    if (img && !img.src.endsWith(`${environment.defaultCountryCode}.png`)) {
      img.src = fallback;
    }
  }

  /**
   * AIO-82318 gap: template-02 intercepts logout while a two-step deposit is still waiting to be
   * paid — "Continue Payment" aborts the logout, "Cancel Deposit" cancels the reservation via
   * /deposit/cancelpendingpayments and only then logs out. There is nothing to hang that on here
   * yet: this body is commented out, so logout does not run at all in this build. Add the check
   * at the top of whatever restores it, reading NewTwostepDepositDataService.getTwostepDeposit().
   */
  onLogout() {
    this.loadingBar.start();
    // authService.logout() already dispatches Logout + UnsetMemberProfile, which is what
    // clears the legacy session keys via the legacySessionKeys meta-reducer.
    this.authService.logout().subscribe({
      next: () => this.redirectAfterLogout(),
      error: () => {
        // The dispatches live in logout()'s tap, which only runs on success. If /logout fails
        // (e.g. 401 on an already-expired token) the stored token survives the reload and the
        // member is logged straight back in -- so end the local session regardless.
        this.store.dispatch(AuthActions.Logout());
        this.store.dispatch(MemberProfileActions.UnsetMemberProfile());
        this.redirectAfterLogout();
      },
    });
  }

  private redirectAfterLogout() {
    this.loadingBar.complete();
    // Read before the reload: app.component's storageForVersion() consumes this flag so a
    // logout is not mistaken for a plain reload by the version check.
    sessionStorage.setItem('loggedOut', 'true');
    const locale = this.domainService.getLocale();
    window.location.href = locale ? `/${locale}/home` : '/';
  }

  onRestore() {
    this.restore(true, true);
  }

  private restore($loading: boolean, $swal: boolean) {
    return new Promise<void>((resolve) => {
      this.turnoverAmount = undefined;
      this.isReload = true;
      $loading ? this.loadingBar.start() : null;

      // The 1s delay is template-02's: /wallet/restore returns before the provider-side
      // transfers have settled, so reading the balance immediately shows a stale figure.
      timer(1000).pipe(switchMap(() => this.walletHttpService.restoreWallet())).subscribe({
        next: (res) => {
          this.memberCurrency = res.currency_code ? res.currency_code : '$';
          if (res.balance) {
            this.walletHttpService.getAmount().subscribe((amount: any) => {
              this.turnoverAmount = amount;
              if (amount['total_promo_turnover'] <= 0 && amount['total_deposit_turnover'] > 0) {
                this.show_promo_turnover = false;
              }
              this.eventEmitterService.onUpdateTurnover(amount);

              this.walletHttpService.getMemberBalance().subscribe((wallet) => {
                this.memberBalance = wallet.balance;
                this.finishRestore($loading, $swal);
                resolve();
              });
            });
          } else {
            this.finishRestore($loading, $swal);
            resolve();
          }
        },
        error: () => {
          // Without this the spinner state latched on and the balance stayed a skeleton.
          this.finishRestore($loading, false);
          resolve();
        },
      });
    });
  }

  private finishRestore($loading: boolean, $swal: boolean) {
    this.isReload = false;
    this.walletLoading = false;
    $loading ? this.loadingBar.complete() : null;
    $swal ? this.onSwal() : null;
  }

  private onSwal() {
    Swal.fire({
      icon: 'success',
      html: '<div class="text-center">' + this.translateService.instant('Refresh Successfully') + ' </div>',
      toast: true,
      showConfirmButton: false,
      showCloseButton: false,
      customClass: this.customClass,
      position: 'top',
      timer: 3000,
      hideClass: {
        popup: `animate__animated animate__bounceOutUp`
      },
    });
  }

  /** Reads the current balance + turnover without triggering a provider-side restore. */
  private async refreshWalletDisplay() {
    await this.getWalletDetails();
    this.getTurnoverAmount();
    this.isReload = false;
    this.walletLoading = false;
  }

  private getWalletDetails() {
    if (window.location.pathname === '/gamelaunch') return Promise.resolve();
    return new Promise<void>((resolve) => {
      this.walletHttpService.getMemberBalance().subscribe({
        next: (res) => {
          this.memberBalance = res.balance;
          this.memberCurrency = res.currency_code ? res.currency_code : '$';
          resolve();
        },
        error: () => resolve(),
      });
    });
  }

  private getTurnoverAmount() {
    if (window.location.pathname === '/gamelaunch') return;
    this.walletHttpService.getAmount().subscribe((res: any) => {
      if (!res) return;
      this.turnoverAmount = res;
      this.show_promo_turnover = !(res['total_promo_turnover'] <= 0 && res['total_deposit_turnover'] > 0);
      this.isReload = false;
      this.walletLoading = false;
    });
  }

  /**
   * `isDepositTurnover` picks the deposit-turnover figures over the promo ones in the
   * turnover tooltip (3 bindings in the template). It was declared but never assigned, so
   * the tooltip always fell back to the promo branch regardless of the BO setting.
   */
  private getDepositTurnoverSetting() {
    this.systemSettingHttpService.getWithQuery('turnover').subscribe((res: any) => {
      this.isDepositTurnover = res?.option?.['1x_turnover_of_deposit'] == 1;
    });
  }

  private subscribeToWalletEvents() {
    this.subscriptions.push(
      this.eventEmitterService.updateCurrencyEmitter.subscribe((res: any) => {
        this.memberCurrency = res?.currency_code ? res.currency_code : '$';
      }),
      this.eventEmitterService.updateBalanceEmitter.subscribe((res: any) => {
        this.memberBalance = res;
        this.walletLoading = false;
      }),
      this.eventEmitterService.updateTurnoverEmitter.subscribe((res: any) => {
        this.turnoverAmount = res;
        this.show_promo_turnover = !(res['total_promo_turnover'] <= 0 && res['total_deposit_turnover'] > 0);
      })
    );
  }

  private subscribeToVersionStatus() {
    this.subscriptions.push(
      merge(
        this.sharedService.versionStatus$,
        this.sharedService.interfaceStatus$
      ).subscribe((event) => {
        if (typeof event === 'string') {
          this.latestVersion = event;
          this.haveNewVersion = localStorage.getItem('version') !== this.latestVersion;
        } else if (!this.haveNewVersion && event === true) {
          this.haveNewVersion = true;
        }
      })
    );
  }

  /**
   * Desktop has no service worker registered (angular.json only configures one for the
   * mobile project), so there is no SwUpdate to activate -- a plain reload picks up the
   * new bundle.
   */
  updateAppInterface() {
    // Pin the version we are reloading into, the way mobile's PopupServiceWorkerComponent
    // does. checkVersionNew() would also write it via the `wasReloadedOrJustVisit` flag,
    // but doing it here means the banner cannot come straight back if that flag is missed.
    if (this.latestVersion) {
      localStorage.setItem('version', this.latestVersion);
    }
    document.location.reload();
  }

  /**
   * Wallet icon in the member bar.
   *
   * template-02 gates this on before_deposit system settings (update profile / verify /
   * add bank) before navigating. As of AIO-82318 those checks are ported: they live in
   * DepositRequirementService and run from depositGuard on the /member/deposit route, so the
   * button only has to navigate — the guard blocks and explains if the member is not eligible.
   */
  onRedirectDeposit() {
    this.router.navigate(['/member/deposit']);
  }

  stickyGameMenu() {
    this.fixed = false;
    if (this.document.documentElement.scrollTop > 91) {
      this.fixed = true;
    }
  }

  onHover(icon: any, mouseIn?: boolean) {
    switch (icon) {
      case 'wallet':
        this.walletIcon = mouseIn ? 'assets/member/wallet.png' : 'assets/topbar/wallet.png';
        break;
      case 'portal':
        this.portalIcon = mouseIn ? 'assets/topbar/account-active.png' : 'assets/topbar/account.png';
        break;
      case 'message':
        this.messageIcon = mouseIn ? 'assets/topbar/message-active.png' : 'assets/topbar/message.png';
        break;
      case 'home':
        this.homeIcon = mouseIn ? 'assets/topbar/home.png' : 'assets/topbar/home.png';
        break;
      case 'mobile':
        this.mobileIcon = mouseIn ? 'assets/topbar/mobile.png' : 'assets/topbar/mobile.png';
        break;
    }
  }

  getRouterLink(link: string) {
    // This function is used to filter the URL link to internal URL path and handle these URL see which menu bar should set to Active
    // /casino, /external?link=https://www.mcbaffiliate.com/
    if (link.startsWith('/external?link=')) {
      return link.replace('/external?link=', '');
    }
    return link;
  }

  checkActiveRoute(link: string): boolean {
    if (!this.platformService.isBrowser()) {
      return false; // or return a default, never use `window` on server
    }

    const baseLink = link.split('?')[0].replace(/\/$/, ''); // remove trailing slash

    // Handle external links
    if (link.startsWith('/external?link=')) {
      return false;
    }

    // Strip the '/:locale' segment (qply routes are '/en-my/referral' while CMS menu links are
    // '/referral'), otherwise no menu item ever matches. Same normalisation as isRouteActive().
    const currentPath = this.router.url.split('?')[0]
      .replace(/^\/[a-z]{2}-[a-z]{2}(?=\/|$)/i, '')
      .replace(/\/$/, '');

    return currentPath === baseLink;
  }

  navigateTo(link: string): void {
    // If URL link is external site URL, use window.location.href to redirect.
    // If the URL is internal site URL, use navigateByUrl method.
    if (link.startsWith('/external?link=') && this.platformService.isBrowser()) {
      window.location.href = link.replace('/external?link=', '');
    } else {
      this.router.navigateByUrl(link);
    }
  }

  isRouteActive(link: string) {
    if (!this.platformService.isBrowser()) return false;
    // Compare the locale-stripped path against the link by whole segment (not a bare substring),
    // so a link like '/casino' matches /en-my/casino and /en-my/casino/<provider> but NOT
    // /en-my/promotion/casino-welcome-bonus. '-game' is normalized on both sides so a '/slots'
    // link still matches /slots-game pages (mirrors the old split('-game') behaviour).
    const norm = (p: string) =>
      (p || '').split('?')[0]
        .replace(/^\/[a-z]{2}-[a-z]{2}(?=\/|$)/i, '')
        .replace(/-game(?=\/|$)/g, '')
        .replace(/\/+$/, '') || '/';
    const path = norm(window.location.pathname);
    const target = norm(link.startsWith('/') ? link : '/' + link);
    return path === target || path.startsWith(target + '/');
  }

  getRouterLinkWithGameProvider(link: string) {
    // Function to return router with game provider for some game category
    let URL = this.getRouterLink(link);

    switch (URL) {
      // case '/slots':
      //   return URL + '/' + this.firstAvailableProviderSL;
      //   break;

      case '/fishing':
        return URL + '/' + this.firstAvailableProviderFS;
        break;

      case '/lottery':
        return URL + '/' + this.firstAvailableProviderLT;
        break;
    }

    return URL;
  }

  @HostListener('window:scroll')
  pageScroll() {
    this.stickyGameMenu();
  }

  @HostListener('window:resize', [])
  onResize() {
    this.fitMenu();
  }

  fitMenu() {

    // get menu bars width
    const element = document.getElementById("menubars");
    const menuBarsWidth = element?.offsetWidth || 0;

    // set min width for a menu
    const minItemWidth = 160;

    // calculate more items
    const menuItems = Math.floor(menuBarsWidth / minItemWidth);
    const moreItemsCount = this.menuLists.length > menuItems ? menuItems - 1 : this.menuLists.length;

    // get menu list and more item list
    this.menu = this.menuLists.slice(0, moreItemsCount);
    this.moreItems = this.menuLists.slice(moreItemsCount);

  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.subscriptions.forEach(sb => sb.unsubscribe());
    this.subscriptions = [];
  }

  getMenuBar() {
    // Start loading menu data
    this.menuBarHttpService.getMenuBar().subscribe((res) => {
      // console.log("getMenuBar : ", res);
      this.menuLists = res;
      this.isSkeletonMenuLists = false;

      // CRITICAL: Only now we have real data → recalculate layout
      if (this.platformService.isBrowser()) {
        // Use requestAnimationFrame or setTimeout to ensure DOM is updated
        requestAnimationFrame(() => this.fitMenu());
      } else {
        // SSR: just show all items (safe)
        this.menu = [...res];
        this.moreItems = [];
      }
    });

    // Setup ResizeObserver early (so it's ready when data arrives)
    if (this.platformService.isBrowser()) {
      this.resizeObserver = new ResizeObserver(() => {
        this.fitMenu();
      });

      if (this.menubars?.nativeElement) {
        this.resizeObserver.observe(this.menubars.nativeElement);
      }

      // Optional: one immediate try in case data was already cached
      if (this.menuLists.length > 0) {
        requestAnimationFrame(() => this.fitMenu());
      }
    }
  }

  flipArrow() {
    return this.document.getElementById('more-button').getAttribute('aria-expanded') === 'true';
  }
}
